import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * ANTLITZWERK cart routing guard.
 *
 * 1. Routes Shopify's explicit open-cart action to the existing drawer.
 * 2. Provides a safe AJAX fallback for product/app forms that would otherwise
 *    submit natively and redirect the customer to /cart.
 *
 * Successful add-to-cart actions deliberately do not auto-open the drawer;
 * cart sections still update through the native CartLinesUpdateEvent promise.
 *
 * Accelerated checkout buttons (Buy now / Shop Pay etc.) are deliberately not
 * intercepted, because their expected destination is checkout rather than cart.
 */

const DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
const CART_ADD_PATH = '/cart/add';
let fallbackRequestInFlight = false;
let actionsConfigured = false;

function antlitzwerkAddErrorMessage() {
  const language = document.documentElement.lang.toLowerCase();
  if (language.startsWith('fr')) return 'Le produit n’a pas pu être ajouté.';
  if (language.startsWith('en')) return 'The product could not be added.';
  return 'Das Produkt konnte nicht hinzugefügt werden.';
}

/** @returns {{ cart_url?: string, cart_add_url?: string }} */
function getThemeRoutes() {
  return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
}

/** @returns {(HTMLElement & { open?: () => void, isOpen?: boolean }) | null} */
function getCartDrawer() {
  return /** @type {(HTMLElement & { open?: () => void, isOpen?: boolean }) | null} */ (
    document.querySelector(DRAWER_SELECTOR)
  );
}

function configureShopifyActions() {
  if (actionsConfigured) return true;

  const actions = window.Shopify?.actions;
  if (!actions) return false;

  actions.updateCart?.configure?.({
    eventTarget: () => getCartDrawer() ?? document,
  });

  actions.openCart?.configure?.({
    async handler() {
      const drawer = getCartDrawer();
      if (drawer?.open) {
        drawer.open();
        return;
      }

      window.location.href = getThemeRoutes().cart_url || '/cart';
    },
  });

  actionsConfigured = true;
  return true;
}

function configureActionsWithoutPolling() {
  if (configureShopifyActions()) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureShopifyActions, { once: true });
  }

  if (document.readyState !== 'complete') {
    window.addEventListener('load', configureShopifyActions, { once: true });
  } else {
    queueMicrotask(configureShopifyActions);
  }
}

/** @param {HTMLFormElement} form */
function isCartAddForm(form) {
  try {
    const action = new URL(form.action || '', window.location.href);
    return action.pathname.includes(CART_ADD_PATH);
  } catch (_) {
    return false;
  }
}

/** @param {HTMLElement | null} submitter */
function isAcceleratedCheckoutSubmitter(submitter) {
  if (!submitter) return false;

  return Boolean(
    submitter.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart') ||
      submitter.matches('[name="checkout"], [data-shopify-buy-it-now-button], .shopify-payment-button__button')
  );
}

/** @param {HTMLFormElement} form */
function collectSectionIds(form) {
  const ids = new Set(['cart-drawer-section']);

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  });

  const sourceSection = form.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
  if (sourceSection) ids.add(sourceSection);

  return Array.from(ids).join(',');
}

/**
 * Handles only forms that no theme/app JavaScript claimed. This is the crucial
 * fallback for forms that would otherwise navigate to the full /cart page.
 *
 * @param {SubmitEvent} event
 */
async function handleUnhandledNativeCartSubmit(event) {
  if (event.defaultPrevented || fallbackRequestInFlight) return;
  if (!(event.target instanceof HTMLFormElement)) return;

  const form = event.target;
  if (!isCartAddForm(form)) return;

  const sourceModal = form.closest('dialog[open]');

  const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
  if (isAcceleratedCheckoutSubmitter(submitter)) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (!form.reportValidity()) return;

  fallbackRequestInFlight = true;
  submitter?.setAttribute('aria-busy', 'true');
  if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) submitter.disabled = true;

  const formData = new FormData(form);
  const variantId = String(formData.get('id') || '');
  const quantity = Math.max(1, Number(formData.get('quantity') || 1));
  const sections = collectSectionIds(form);

  formData.set('sections', sections);
  formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: variantId ? [{ merchandiseId: variantId, quantity }] : [],
      promise: deferred.promise,
      detail: {
        source: 'antlitzwerk-cart-fallback',
        sourceId: form.id || undefined,
      },
    })
  );

  try {
    const response = await fetch(getThemeRoutes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const added = await response.json();
    if (!response.ok || added?.status) {
      const message = added?.description || added?.message || antlitzwerkAddErrorMessage();
      throw new Error(message);
    }

    const cartResponse = await fetch(`${getThemeRoutes().cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections || {},
        items: cart.items,
        itemCount: cart.item_count,
        source: 'antlitzwerk-cart-fallback',
        sourceId: form.id || undefined,
        didError: false,
      },
    });

    if (sourceModal instanceof HTMLDialogElement && sourceModal.open) {
      sourceModal.close();
    }
  } catch (error) {
    deferred.reject(error);
    form.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : antlitzwerkAddErrorMessage(),
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    console.error('[ANTLITZWERK cart] Fallback add failed:', error);
  } finally {
    fallbackRequestInFlight = false;
    submitter?.removeAttribute('aria-busy');
    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) submitter.disabled = false;
  }
}

configureActionsWithoutPolling();
document.addEventListener('submit', handleUnhandledNativeCartSubmit);
